import { Pagination } from "@/components/atoms/Pagination/pagination"
import { PaginationContent } from "@/components/atoms/Pagination/pagination-content"
import { PaginationItem } from "@/components/atoms/Pagination/pagination-item"
import { PaginationLink } from "@/components/molecules/Pagination/pagination-link"
import { PaginationNext } from "@/components/molecules/Pagination/pagination-next"
import { PaginationPrevious } from "@/components/molecules/Pagination/pagination-previous"

const PAGE_OFFSET = 2

interface PaginationSectionProps {
  queryParams: Record<string, string | string[] | undefined | number>
  totalPages: number
}

const PaginationContainer = ({ queryParams, totalPages }: PaginationSectionProps) => {
  const currentPage = +(queryParams.page || 1);

  // Clamp startPage and endPage within valid bounds
  let startPage = Math.max(1, currentPage - PAGE_OFFSET);
  let endPage = Math.min(totalPages, currentPage + PAGE_OFFSET);

  // Adjust startPage if endPage exceeds totalPages
  if (endPage === totalPages) {
    startPage = Math.max(1, endPage - PAGE_OFFSET * 2);
  }

  // Generate an array of page numbers to display
  const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  // Hide pagination if there's only one page
  if (totalPages <= 1) {
    return null;
  }

  // Clean up queryParams to remove empty values and properly format them
  const cleanQueryParams = Object.entries(queryParams).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)) {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, any>);

  return (
    <Pagination>
      {/* Previous Button */}
      <PaginationPrevious
        aria-label="Go to previous page"
        disabled={currentPage === 1}
        href={{
          query: {
            ...cleanQueryParams,
            page: currentPage > 1 ? currentPage - 1 : undefined,
          },
        }}
      />

      {/* Pagination Content */}
      <PaginationContent>
        {pages.map((pageNumber) => (
          <PaginationItem key={`pagination_item_${pageNumber}`}>
            <PaginationLink
              aria-current={pageNumber === currentPage ? "page" : undefined}
              isActive={pageNumber === currentPage}
              href={{
                query: {
                  ...cleanQueryParams,
                  page: pageNumber,
                },
              }}
            >
              {pageNumber}
            </PaginationLink>
          </PaginationItem>
        ))}
      </PaginationContent>

      {/* Next Button */}
      <PaginationNext
        aria-label="Go to next page"
        disabled={currentPage === totalPages}
        href={{
          query: {
            ...cleanQueryParams,
            page: currentPage < totalPages ? currentPage + 1 : undefined,
          },
        }}
      />
    </Pagination>
  );
};

export {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationContainer,
}
